import TaskHeader from "components/task/header/TaskHeader";
import { Box, Stack, Typography } from "@mui/joy";
import { useRecoilValue, useResetRecoilState, useSetRecoilState } from "recoil";
import { userAtom } from "recoil/userAtom";
import { theme } from "styles/theme";
import CustomTypography from "components/common/CustomTypography";
import ContentTemplateV2 from "components/task/content/ContentTemplateV2";
import CustomModal from "components/common/CustomModal";
import { useNavigate } from "react-router-dom";
import { patientAtom } from "recoil/patientAtom";
import { programDataAtom } from "recoil/programDataAtom";
import { taskListAtom } from "recoil/taskListAtom";
import { onboardingDataState } from "recoil/onboardingDataAtom";
import { homeThoughtRecordListAtom } from "recoil/homeThoughtRecordListAtom";
import {
  appLockEnabledAtom,
  appLockPinHashAtom,
  appBiometricEnabledAtom,
} from "recoil/appLockAtoms";
import { sendMessage } from "utils/webview";
import mixpanel from "mixpanel-browser";
import useIsInappWebview from "hooks/useIsInappWebview";
import useSubscriptionStatus from "hooks/useSubscriptionStatus";
import { isDev } from "config";
import React, { useEffect, useState } from "react";
import useFetch from "hooks/useFetch";
import FullPageContainer from "../container/FullPageContainer";
import BackButton from "../../components/common/BackButton";
import LinkV2 from "../../components/common/LinkV2";
import useGetDeviceInfo from "../../hooks/useGetDeviceInfo";
import { extractHexColor } from "../../styles/hexOpacity";
import { useDisableAppLock } from "../../api/appLockQueries";

export default function AccountPage() {
  const user = useRecoilValue(userAtom);
  const isInappWebview = useIsInappWebview();
  const { isSubscribing } = useSubscriptionStatus();
  const isFullPage = window.location.pathname === "/mypage/account";
  const navigate = useNavigate();
  const { homeIndicatorHeight } = useGetDeviceInfo();

  // State for modal visibility
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [deleteAccountModalOpen, setDeleteAccountModalOpen] = useState(false);
  const backgroundColor = theme.vars.palette.Background.Level1;

  // State for loading indicators
  const [isLoading, setIsLoading] = useState(false);

  // Fetch hooks for API calls
  const [logoutData, fetchLogout] = useFetch({
    url: "/user/logout",
  });

  const [deleteAccountData, fetchDeleteAccount] = useFetch({
    url: "/user/leave",
  });

  const resetUser = useResetRecoilState(userAtom);
  const resetPid = useResetRecoilState(patientAtom);
  const resetProgramData = useResetRecoilState(programDataAtom);
  const resetTaskState = useResetRecoilState(taskListAtom);
  const resetOnboardingData = useResetRecoilState(onboardingDataState);
  const resetHomeThoughtRecordState = useResetRecoilState(
    homeThoughtRecordListAtom
  );
  // App Lock states
  const setAppLockEnabled = useSetRecoilState(appLockEnabledAtom);
  const setAppBiometricEnabled = useSetRecoilState(appBiometricEnabledAtom);
  const resetAppLockPinHash = useResetRecoilState(appLockPinHashAtom);

  const initializeUser = () => {
    resetUser();
    resetPid();
    resetProgramData();
    resetTaskState();
    resetOnboardingData();
    resetHomeThoughtRecordState();
    setAppLockEnabled(false);
    setAppBiometricEnabled(false);
    resetAppLockPinHash();

    if (isInappWebview) {
      sendMessage(
        JSON.stringify({
          event: "set_app_lock",
          params: { isEnabled: "false" },
        })
      );
    }

    window.location.href = `${window.location.origin}/login`;
    sendMessage(
      JSON.stringify({
        event: "logout",
      })
    );
    mixpanel.reset();
  };

  // Handle logout action
  const handleLogout = async () => {
    setIsLoading(true);
    await fetchLogout();
    setIsLoading(false);
    setLogoutModalOpen(false);
    initializeUser();
  };

  // Handle account deletion action
  const handleDeleteAccount = async () => {
    setIsLoading(true);
    await fetchDeleteAccount();
    setIsLoading(false);
    setDeleteAccountModalOpen(false);
    initializeUser();
  };

  useEffect(() => {
    if (isInappWebview) {
      const hexValue = extractHexColor(backgroundColor);

      if (hexValue) {
        sendMessage(
          JSON.stringify({
            event: "set_safearea_color",
            params: {
              topColor: `${hexValue}`,
            },
          })
        );
      }
    }
  }, []);

  const child = (
    <>
      <Stack spacing={2}>
        <Stack
          justifyContent="space-between"
          sx={{
            borderRadius: "16px",
            backgroundColor: theme.vars.palette.Surface.Forward,
            p: 1,
          }}
        >
          <Stack
            direction={"row"}
            width={"100%"}
            height={"48px"}
            justifyContent="space-between"
            alignItems="center"
            sx={{ px: "12px" }}
          >
            <CustomTypography
              variant={"Body"}
              sx={{
                fontWeight: 500,
                color: theme.vars.palette.Content.Medium,
              }}
            >
              {"회원번호"}
            </CustomTypography>
            {user?.userId && (
              <CustomTypography
                variant={"Body"}
                sx={{
                  fontWeight: 700,
                  color: theme.vars.palette.Content.Strong,
                }}
              >
                {user?.userId}
              </CustomTypography>
            )}
          </Stack>
          <Stack
            direction={"row"}
            width={"100%"}
            height={"48px"}
            justifyContent="space-between"
            alignItems="center"
            sx={{ px: "12px" }}
          >
            <CustomTypography
              variant={"Body"}
              sx={{
                fontWeight: 500,
                color: theme.vars.palette.Content.Medium,
              }}
            >
              {"닉네임"}
            </CustomTypography>
            {user?.nickname && (
              <CustomTypography
                variant={"Body"}
                sx={{
                  fontWeight: 700,
                  color: theme.vars.palette.Content.Strong,
                }}
              >
                {user?.nickname}
              </CustomTypography>
            )}
          </Stack>
        </Stack>
        <Box
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            pb: `${isFullPage ? 40 : homeIndicatorHeight + 40}px`,
            zIndex: 10,
          }}
        >
          <Stack
            direction={"row"}
            justifyContent="center"
            alignItems="center"
            spacing="20px"
          >
            <div onClick={() => setLogoutModalOpen(true)}>
              <LinkV2 text={"로그아웃"} />
            </div>
            <div onClick={() => setDeleteAccountModalOpen(true)}>
              <LinkV2 text={"계정 삭제"} />
            </div>
          </Stack>
        </Box>
      </Stack>

      {/* Logout Modal */}
      <CustomModal
        open={logoutModalOpen}
        onClose={() => setLogoutModalOpen(false)}
        title={"정말로 로그아웃할까요?"}
        subtitle={"모든 기기와 브라우저에서 로그아웃돼요."}
        actions={[
          {
            label: "로그아웃하기",
            onClick: handleLogout,
            variant: "Soft",
            color: "danger",
            buttonProps: {
              loading: isLoading,
            },
          },
        ]}
      />

      {/* Account Deletion Modal */}
      <CustomModal
        open={deleteAccountModalOpen}
        onClose={() => setDeleteAccountModalOpen(false)}
        title={
          !isDev && isSubscribing
            ? "지금은 삭제할 수 없습니다"
            : "정말로 삭제할까요?"
        }
        subtitle={
          !isDev && isSubscribing
            ? "프로그램 이용 종료 후 다시 시도해주세요."
            : "모든 정보와 지난 활동 내역이 삭제되며 돌이킬 수 없어요."
        }
        actions={
          !isDev && isSubscribing
            ? [
                {
                  label: "확인",
                  onClick: () => setDeleteAccountModalOpen(false),
                  variant: "Solid",
                  color: "primary",
                },
              ]
            : [
                {
                  label: "삭제하기",
                  onClick: handleDeleteAccount,
                  variant: "Solid",
                  color: "danger",
                  buttonProps: {
                    loading: isLoading,
                  },
                },
              ]
        }
      />
    </>
  );

  return isFullPage ? (
    <FullPageContainer backgroundColor={backgroundColor}>
      <Stack
        direction={"column"}
        justifyContent={"flex-start"}
        sx={{
          maxWidth: "660px",
          width: "100vw",
          mx: "auto",
          height: "100vh",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            width: "100%",
            position: "sticky",
            top: 0,
            zIndex: 100,
            height: `60px`, // 헤더 높이 설정
            px: 1,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <BackButton onBack={() => navigate(-1)} />
            <Typography
              variant="Body"
              sx={{
                px: 0,
                fontColor: theme.vars.palette.Content.Strong,
                fontWeight: 700,
              }}
            >
              {"계정"}
            </Typography>
          </Stack>
        </Stack>
        <Stack
          sx={{
            width: "100%",
            flex: 1,
            overflow: "auto",
            px: `20px`,
            py: 0,
            position: "relative",
          }}
        >
          {child}
        </Stack>
      </Stack>
    </FullPageContainer>
  ) : (
    <>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 100,
        }}
      >
        <TaskHeader
          key={`account_${user?.userId}`}
          taskKey={`mypage-account-${user?.userId}`}
          title={"계정"}
        />
      </Box>

      <ContentTemplateV2>{child}</ContentTemplateV2>
    </>
  );
}
